/* U Press — AI Mockup Generator, compositing core.
 *
 * One idea runs through this file: the artwork lives in the garment's own
 * coordinate space, not the image's. The chest is found once and described as a
 * quad; the customer's placement is a position and width INSIDE that quad. So
 * moving a design across the chest keeps the garment's perspective and keeps
 * following its folds, instead of sliding a flat rectangle around.
 *
 * Every render starts from the original uploaded file. Nothing is ever
 * re-composited from a previous composite, so repeated adjustments cannot
 * accumulate blur.
 *
 * No DOM at the top: the analysis works on plain ImageData-shaped objects so it
 * can be run under Node for tests.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.UPMCore = api;
}(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var C = {
    /* Where to look for a torso, as fractions of the image. Generous: the
     * chest is found inside this, not assumed to fill it. */
    BAND_TOP: 0.18, BAND_BOTTOM: 0.80,
    /* How close a pixel must be to the garment colour to count as garment. */
    /* The garment surface is decided by COLOUR, not by brightness. A fold is
     * the same cloth in less light; a flat per-channel tolerance throws it
     * out and then the print gets clipped away there and the fold gets
     * drawn over the print. Measured on the olive sweatshirt that showed
     * the defect: flat chest 3.8, sleeve shadow 5.6, a fold 7.5, the
     * deepest part of that fold 10.9 - against skin at 14.7 and hair at
     * 22.5. Thirteen sits in the gap. */
    TOL_SHADE: 20, TOL_CHROMA: 15, TOL_DARKER: 150, TOL_LIGHTER: 130,
    /* Mesh density. Preview is coarser; the export is fine. */
    MESH_PREVIEW: 14, MESH_FINAL: 36,
    /* Fabric treatment. Deliberately gentle - these are clean transfers, not
     * distressed vintage prints. */
    DISPLACE_FRAC: 0.012,   /* of print width */
    SHADE: 0.55,            /* how much of the garment's own light shows */
    SHADE_FLOOR: 0.80, SHADE_CEIL: 1.14,
    INK: 1.0,               /* no global darkening */
    /* The printable chest, as proportions of shoulder width. These are the
     * numbers a print shop works to, not guesses about the picture. */
    QUAD_W_OF_SHOULDER: 0.88, QUAD_TOP_OF_SHOULDER: 0.17, QUAD_H_OF_W: 1.22,
    /* Flat lay and hanging garment have no shoulders to measure from, so the
     * print area comes off the garment's own body width, taken below the
     * sleeves where the silhouette is the body and nothing else. */
    GARMENT_BODY_AT: 0.62, GARMENT_W_OF_BODY: 0.80, GARMENT_TOP_OF_H: 0.22,
    /* Default placement, as a fraction of that quad. */
    DEFAULT_WIDTH: 0.92, DEFAULT_CX: 0.5, DEFAULT_CY: 0.40
  };

  function lum(r, g, b) { return 0.2126 * r + 0.7152 * g + 0.0722 * b; }

  /* ----------------------------------------------------------------- blur */
  function boxBlur(src, w, h, radius) {
    if (radius < 1) return src.slice(0);
    var tmp = new Float32Array(w * h), out = new Float32Array(w * h), i, x, y, sum, n;
    for (y = 0; y < h; y++) {
      sum = 0; n = 0;
      for (x = -radius; x <= radius; x++) { if (x >= 0 && x < w) { sum += src[y * w + x]; n++; } }
      for (x = 0; x < w; x++) {
        tmp[y * w + x] = sum / n;
        var add = x + radius + 1, rem = x - radius;
        if (add < w) { sum += src[y * w + add]; n++; }
        if (rem >= 0) { sum -= src[y * w + rem]; n--; }
      }
    }
    for (x = 0; x < w; x++) {
      sum = 0; n = 0;
      for (y = -radius; y <= radius; y++) { if (y >= 0 && y < h) { sum += tmp[y * w + x]; n++; } }
      for (y = 0; y < h; y++) {
        out[y * w + x] = sum / n;
        var addy = y + radius + 1, remy = y - radius;
        if (addy < h) { sum += tmp[addy * w + x]; n++; }
        if (remy >= 0) { sum -= tmp[remy * w + x]; n--; }
      }
    }
    return out;
  }

  /* ------------------------------------------------------- garment surface */

  /* Decide what counts as garment by sampling the colour at the chest itself,
   * so this works on black, white, grey, navy or red without a special case. */
  function garmentColour(data, w, h) {
    var cx = (w / 2) | 0, y0 = (h * 0.34) | 0, y1 = (h * 0.52) | 0;
    var rs = [], gs = [], bs = [], x, y, i;
    for (y = y0; y < y1; y += 2) {
      for (x = cx - ((w * 0.07) | 0); x < cx + ((w * 0.07) | 0); x += 2) {
        i = (y * w + x) * 4; rs.push(data[i]); gs.push(data[i + 1]); bs.push(data[i + 2]);
      }
    }
    var med = function (a) { a.sort(function (p, q) { return p - q; }); return a[(a.length / 2) | 0]; };
    return { r: med(rs), g: med(gs), b: med(bs) };
  }

  /* Which pixels are the garment's own surface.
   *
   * A fold is the same cloth with less light falling on it. That is a
   * MULTIPLICATION of the garment's colour, not a shift of it, so the test is:
   * scale the garment's colour to this pixel's brightness, and ask how far the
   * pixel is from that prediction. Shadows and highlights predict well; hair,
   * skin and anything printed on the cloth do not.
   *
   * The flat per-channel tolerance this replaces threw folds out, which both
   * clipped the print away inside them and painted them back over the top - the
   * ragged bite out of the G in GIVEN. Measured on that garment: 99.7% of flat
   * chest was inside the old mask but only 18% of the deepest fold.
   *
   * Scaling rather than chromaticity matters on a near-black garment, where a
   * hue is mostly sensor noise but a brightness ratio is still meaningful. */
  /* Close the hairline gaps a fold leaves in the garment mask.
   *
   * A crease is still cloth, but at its darkest a few pixels across it can fall
   * outside any colour test. Those few pixels do real damage twice over: the
   * print is CLIPPED away along the crease, and the same pixels are then drawn
   * back OVER the print by the foreground layer - so a fold does not shade the
   * artwork, it cuts a line through it. Measured on the hanging hoodie with a
   * solid rectangle: 3.4% of the artwork clipped away and another 1.5% painted
   * over, all of it along crease lines.
   *
   * A morphological closing - grow, then shrink back by the same amount -
   * bridges a gap narrower than the brush without moving the garment's outer
   * edge. The brush is deliberately small: wide enough for a crease, too narrow
   * to swallow a drawstring, which has to stay in front of the print. */
  /* Colour only: the garment's hue, at whatever brightness. No lightness
   * bounds and no shading residual, both of which a deep crease fails. */
  function chromaMask(data, w, h, col, scale) {
    var T = col.r + col.g + col.b;
    if (T < 12) T = 12;
    var CR = col.r / (T + 60), CG = col.g / (T + 60);
    var allow = C.TOL_CHROMA * Math.max(1, 380 / T) * (scale === undefined ? 1 : scale);
    var out = new Uint8Array(w * h), i, p, r, g, b, t, dc, pixAllow;
    for (i = 0, p = 0; p < out.length; p++, i += 4) {
      r = data[i]; g = data[i + 1]; b = data[i + 2];
      t = r + g + b + 60;
      dc = Math.hypot(r / t - CR, g / t - CG) * 255;
      /* Hue is only as trustworthy as the light on THIS pixel, not just on the
       * garment as a whole. In the deep shadow of a crease - where a sleeve
       * folds against the body - there is barely any signal left, and holding
       * that pixel to the same tolerance as lit fabric is what let creases be
       * read as foreign objects and painted over the artwork. The allowance
       * opens up as the pixel darkens, which is where the uncertainty actually
       * is; a bright thing in front, like hair on a dark garment, is unaffected. */
      pixAllow = allow * Math.max(1, 320 / t);
      out[p] = dc <= pixAllow ? 1 : 0;
    }
    return out;
  }

  /* Grow a mask outward by a radius. Used to forgive a matte that cuts inside
   * the thing it is supposed to outline. */
  function growMask(mask, w, h, radius) {
    var n = w * h, f = new Float32Array(n), i;
    for (i = 0; i < n; i++) f[i] = mask[i];
    var g = boxBlur(f, w, h, radius);
    var out = new Uint8Array(n);
    for (i = 0; i < n; i++) out[i] = g[i] > 0.0001 ? 1 : 0;
    return out;
  }

  /* Opening: erode, then dilate. It removes the thin fingers a colour-based
   * surface grows into whatever is next to the garment - a pink shirt over
   * cream trousers leaves a fringe of jagged spikes along the hem, and the
   * print inherits every one of them as a torn-paper edge. Erosion cuts
   * anything narrower than the brush; the dilation puts the rest back. */
  function openMask(mask, w, h, radius) {
    var n = w * h, f = new Float32Array(n), i;
    for (i = 0; i < n; i++) f[i] = mask[i];
    var shrunk = boxBlur(f, w, h, radius);
    for (i = 0; i < n; i++) f[i] = shrunk[i] > 0.9999 ? 1 : 0;
    var grown = boxBlur(f, w, h, radius);
    var out = new Uint8Array(n);
    for (i = 0; i < n; i++) out[i] = grown[i] > 0.0001 ? 1 : 0;
    return out;
  }

  function closeMask(mask, w, h, radius) {
    var n = w * h, f = new Float32Array(n), i;
    for (i = 0; i < n; i++) f[i] = mask[i];
    var grown = boxBlur(f, w, h, radius);
    for (i = 0; i < n; i++) f[i] = grown[i] > 0.0001 ? 1 : 0;
    var shrunk = boxBlur(f, w, h, radius);
    var out = new Uint8Array(n);
    for (i = 0; i < n; i++) out[i] = shrunk[i] > 0.9999 ? 1 : 0;
    /* Never let closing remove garment the colour test already accepted. */
    for (i = 0; i < n; i++) if (mask[i]) out[i] = 1;
    return out;
  }

  /* The garment's surface as a REGION, not as a per-pixel colour test.
   *
   * Hue alone cannot do this job on a brown, tan or beige garment, and that is
   * not a tuning problem - skin and blonde hair ARE the same hue as brown
   * cloth. Asking "is this pixel the garment's colour" marks a face, a neck and
   * a head of hair as fabric, so the print is allowed to paint straight over
   * them. On a black or white garment the same test looks perfect, which is
   * exactly why it survived this long.
   *
   * What separates a deep fold from a hairline is not colour and not
   * brightness, it is the EDGE between them. Cloth shades smoothly: a fold is a
   * gradient, however dark it gets. A hairline, a jaw, a collar against a neck
   * is a step. So the surface is grown outward from cloth we are confident
   * about, through anything of the garment's colour, and the growth is stopped
   * by strong edges. A fold is entered because nothing sharp stands in the way;
   * the face is not, because the neckline does.
   *
   * The seeds have to be places we know are cloth WITHOUT asking about colour,
   * because on this garment colour is exactly what cannot be trusted - the
   * strict mask, brightness and all, marks the whole face. So they come from
   * the garment quad: points on the chest, by construction.
   *
   *   seeds - points on the garment, from its own quad
   *   into  - the loose mask: the garment's colour at any brightness
   *   bar   - set by the garment's own edges, so no absolute number is involved
   */
  function connectSurface(seeds, into, data, w, h) {
    var n = w * h, i, p, x, y;
    var L = new Float32Array(n);
    for (p = 0, i = 0; p < n; p++, i += 4) L[p] = lum(data[i], data[i + 1], data[i + 2]);
    var Ls = boxBlur(L, w, h, 1), grad = new Float32Array(n), gx, gy;
    for (y = 1; y < h - 1; y++) {
      for (x = 1; x < w - 1; x++) {
        p = y * w + x;
        gx = Ls[p + 1] - Ls[p - 1];
        gy = Ls[p + w] - Ls[p - w];
        grad[p] = Math.sqrt(gx * gx + gy * gy);
      }
    }
    /* The bar comes from the cloth itself. Sampled on a lattice so a big
     * picture costs no more than a small one. */
    /* The bar comes from the cloth around the seeds, sampled on a lattice so a
     * big picture costs no more than a small one. */
    var gs = [], k, sp;
    for (k = 0; k < seeds.length; k++) {
      sp = seeds[k];
      for (y = Math.max(1, sp[1] - 24); y < Math.min(h - 1, sp[1] + 24); y += 3) {
        for (x = Math.max(1, sp[0] - 24); x < Math.min(w - 1, sp[0] + 24); x += 3) {
          if (into[y * w + x]) gs.push(grad[y * w + x]);
        }
      }
    }
    if (gs.length < 64) return into;
    gs.sort(function (a, b) { return a - b; });
    var bar = Math.max(4, gs[Math.floor(gs.length * 0.98)] * 1.6);

    var out = new Uint8Array(n), q = new Int32Array(n), head = 0, tail = 0;
    for (k = 0; k < seeds.length; k++) {
      p = seeds[k][1] * w + seeds[k][0];
      if (p >= 0 && p < n && into[p] && !out[p]) { out[p] = 1; q[tail++] = p; }
    }
    if (!tail) return into;
    while (head < tail) {
      p = q[head++]; x = p % w; y = (p - x) / w;
      if (x > 0) { i = p - 1; if (!out[i] && into[i] && grad[i] <= bar) { out[i] = 1; q[tail++] = i; } }
      if (x < w - 1) { i = p + 1; if (!out[i] && into[i] && grad[i] <= bar) { out[i] = 1; q[tail++] = i; } }
      if (y > 0) { i = p - w; if (!out[i] && into[i] && grad[i] <= bar) { out[i] = 1; q[tail++] = i; } }
      if (y < h - 1) { i = p + w; if (!out[i] && into[i] && grad[i] <= bar) { out[i] = 1; q[tail++] = i; } }
    }
    return out;
  }

  /* The garment's outline with its interior holes filled in.
   *
   * A cord lies ON the garment, and the region growth stops at the cord's edges
   * - correctly - so the cord becomes a hole in the garment mask. Searching for
   * cords inside that mask therefore looks everywhere except where they are.
   * Filling the holes gives the area a cord can be in. */
  function fillHoles(mask, w, h) {
    var n = w * h, out = new Uint8Array(n), seen = new Uint8Array(n);
    var q = new Int32Array(n), head = 0, tail = 0, p, x, y;
    for (x = 0; x < w; x++) {
      if (!mask[x]) { seen[x] = 1; q[tail++] = x; }
      p = (h - 1) * w + x;
      if (!mask[p] && !seen[p]) { seen[p] = 1; q[tail++] = p; }
    }
    for (y = 0; y < h; y++) {
      p = y * w;
      if (!mask[p] && !seen[p]) { seen[p] = 1; q[tail++] = p; }
      p = y * w + w - 1;
      if (!mask[p] && !seen[p]) { seen[p] = 1; q[tail++] = p; }
    }
    while (head < tail) {
      p = q[head++]; x = p % w; y = (p - x) / w;
      if (x > 0 && !mask[p - 1] && !seen[p - 1]) { seen[p - 1] = 1; q[tail++] = p - 1; }
      if (x < w - 1 && !mask[p + 1] && !seen[p + 1]) { seen[p + 1] = 1; q[tail++] = p + 1; }
      if (y > 0 && !mask[p - w] && !seen[p - w]) { seen[p - w] = 1; q[tail++] = p - w; }
      if (y < h - 1 && !mask[p + w] && !seen[p + w]) { seen[p + w] = 1; q[tail++] = p + w; }
    }
    for (p = 0; p < n; p++) out[p] = (mask[p] || !seen[p]) ? 1 : 0;
    return out;
  }

  /* Cords, by their own shape.
   *
   * The pass this replaces decided a row at a time: for each scanline it looked
   * for a pair of edges and filled between them. That finds a drawstring, but it
   * renders one as a stack of independent horizontal runs - so the width jumps
   * from row to row, a row that fails leaves a gap, and the result on Dennis's
   * brown hoodie was two blocky bars with square steps and loose fragments
   * instead of two cords. No threshold fixes that; the shape of the answer was
   * wrong.
   *
   * This asks the same question without reference to rows. A cord is a THIN
   * structure: subtract a blurred copy of the picture and a thin line survives
   * while a broad fold, which is low frequency however deep it is, cancels out.
   * That response is isotropic, so it follows the cord round its curve and down
   * its length as one continuous shape.
   *
   * Then the decision is made per SHAPE, not per pixel: the surviving pixels are
   * grouped into connected components and a component is kept only if it is long
   * and thin and lies on the garment. Fabric noise is small and round; a seam is
   * short; a cord is long, narrow and continuous. Nothing here looks at colour,
   * which is the other half of why the old pass could not tell a brown cord from
   * brown cloth.
   */
  function cordMask(lum, w, h, garment, scale) {
    var n = w * h, i, p, x, y;
    /* Measured, not guessed. Luminance profiles taken straight across the cords
     * on the three hoodie fixtures: a cord is 6-15 px wide at 2048, and its
     * contrast against the cloth runs from 24 levels (grey cord on black) to 89
     * (white on white, where an edge shadow is all there is). An earlier attempt
     * assumed 20-25 px and smoothed to match, which erased what it was looking
     * for.
     *
     * Running the same filter at three widths and taking the strongest response
     * was tried and was WORSE - normalising across scales let the widest filter
     * dominate and the black hoodie's cords disappeared entirely. One width,
     * taken from the measurement, is what is here. */
    scale = scale || Math.max(5, Math.round(w / 200));
    var sigma = Math.max(1, Math.round(scale * 0.3));

    /* Two box passes approximate a Gaussian closely enough here and cost a
     * fraction of a real one at this size. */
    var g1 = boxBlur(lum, w, h, sigma);
    g1 = boxBlur(g1, w, h, sigma);

    /* Second derivatives. A cord is a ridge or a groove in the brightness
     * surface: strongly curved across its width, almost flat along its length.
     * That is a statement about SHAPE, so it holds for a light cord on dark
     * cloth and a white cord on white cloth alike, and it does not care what
     * colour either of them is - which is the whole difficulty here. */
    var S = new Float32Array(n), ratio = new Float32Array(n);
    var xx, yy, xy, a2, b2, d2, l1, l2, t;
    for (y = 1; y < h - 1; y++) {
      for (x = 1; x < w - 1; x++) {
        p = y * w + x;
        xx = g1[p - 1] - 2 * g1[p] + g1[p + 1];
        yy = g1[p - w] - 2 * g1[p] + g1[p + w];
        xy = (g1[p + w + 1] - g1[p + w - 1] - g1[p - w + 1] + g1[p - w - 1]) * 0.25;
        a2 = (xx + yy) * 0.5;
        b2 = (xx - yy) * 0.5;
        d2 = Math.sqrt(b2 * b2 + xy * xy);
        l1 = a2 + d2; l2 = a2 - d2;
        if (Math.abs(l1) > Math.abs(l2)) { t = l1; l1 = l2; l2 = t; }
        S[p] = Math.sqrt(l1 * l1 + l2 * l2);
        ratio[p] = Math.abs(l2) > 1e-6 ? Math.abs(l1) / Math.abs(l2) : 1;
      }
    }

    /* A cord lies ON the garment, and the region growth that defines the
     * garment stops at the cord's own edges - so the cord is a hole in that
     * mask and searching inside it looks everywhere except where cords are. */
    var area = fillHoles(garment, w, h);

    var samp = [], step = Math.max(1, Math.round(w / 400));
    for (y = 0; y < h; y += step) {
      for (x = 0; x < w; x += step) {
        p = y * w + x;
        if (area[p]) samp.push(S[p]);
      }
    }
    if (samp.length < 256) return new Uint8Array(n);
    samp.sort(function (a, b) { return a - b; });
    var med = samp[Math.floor(samp.length * 0.5)];
    var spread = Math.max(1e-4, samp[Math.floor(samp.length * 0.9)] - med);
    var bar = med + spread * 0.9;

    /* Ridge-like: curved hard across, flat along. The RATIO does the work, so
     * the strength only has to clear ordinary fabric rather than compete with a
     * hood opening - which is what put the bar above the cords before. The
     * weakest cord measured is 24 levels against black cloth, so the bar has to
     * sit well under that and let the shape test reject what it lets through.
     *
     * No morphological closing here. A box-kernel close was what gave the mask
     * square steps, and square steps on a cord are the defect being fixed - the
     * Hessian response is already continuous along a cord, so it does not need
     * one. */
    var hot = new Uint8Array(n);
    for (p = 0; p < n; p++) {
      if (area[p] && S[p] > bar && ratio[p] < 0.7) hot[p] = 1;
    }

    /* Then the decision is per SHAPE. Fabric grain is small and round, a seam
     * is short, a cord is long, narrow and continuous. Nothing is decided a row
     * at a time, so what comes out follows the cord's own contour instead of
     * being a stack of independent horizontal runs - which is what produced the
     * square-edged bars across Dennis's family photo. */
    var out = new Uint8Array(n), seen = new Uint8Array(n);
    var q = new Int32Array(n), comp = new Int32Array(n);
    var minLong = Math.max(24, Math.round(h / 20));
    for (var s0 = 0; s0 < n; s0++) {
      if (!hot[s0] || seen[s0]) continue;
      var head = 0, tail = 0, cn = 0, x0 = w, x1 = -1, y0 = h, y1 = -1;
      q[tail++] = s0; seen[s0] = 1;
      while (head < tail) {
        p = q[head++]; comp[cn++] = p;
        x = p % w; y = (p - x) / w;
        if (x < x0) x0 = x; if (x > x1) x1 = x;
        if (y < y0) y0 = y; if (y > y1) y1 = y;
        if (x > 0 && hot[p - 1] && !seen[p - 1]) { seen[p - 1] = 1; q[tail++] = p - 1; }
        if (x < w - 1 && hot[p + 1] && !seen[p + 1]) { seen[p + 1] = 1; q[tail++] = p + 1; }
        if (y > 0 && hot[p - w] && !seen[p - w]) { seen[p - w] = 1; q[tail++] = p - w; }
        if (y < h - 1 && hot[p + w] && !seen[p + w]) { seen[p + w] = 1; q[tail++] = p + w; }
      }
      var cw = x1 - x0 + 1, chh = y1 - y0 + 1;
      var longSide = Math.max(cw, chh), shortSide = Math.max(1, Math.min(cw, chh));
      var thickness = cn / Math.max(1, longSide);
      if (longSide >= minLong && longSide / shortSide >= 3 &&
          thickness <= scale * 2.0) {
        for (i = 0; i < cn; i++) out[comp[i]] = 1;
      }
    }
    return out;
  }

  function makeMask(data, w, h, col) {
    var T = col.r + col.g + col.b;
    if (T < 12) T = 12;
    var baseL = lum(col.r, col.g, col.b);
    var CR = col.r / (T + 60), CG = col.g / (T + 60);
    /* How far the colour test is allowed to stretch depends on how dark the
     * GARMENT is, not on how dark each pixel is. On a near-black hoodie hue is
     * mostly sensor noise, so leaning on it opens holes everywhere; on an olive
     * sweatshirt it is the only thing that separates a deep fold from hair.
     * Judging it per pixel instead rescued the hair along with the fold. */
    var chromaAllow = C.TOL_CHROMA * Math.max(1, 380 / T);
    var mask = new Uint8Array(w * h), i, p, r, g, b, k, res, dl, t, dc, allow;
    for (i = 0, p = 0; p < mask.length; p++, i += 4) {
      r = data[i]; g = data[i + 1]; b = data[i + 2];
      k = (r + g + b) / T;
      res = Math.max(Math.abs(r - col.r * k),
                     Math.abs(g - col.g * k),
                     Math.abs(b - col.b * k));
      /* The scaling test alone cannot separate dark hair from a deep fold on an
       * olive garment - both predict within 8 levels. Colour can: measured on
       * that garment, the fold sits at 10.9 and the hair at 22.5. So both tests
       * must pass. Fifteen was chosen by sweeping it against the things it has
       * to get right at once: keep the fold, drop the hair, drop the skin. */
      t = r + g + b + 60;
      dc = Math.hypot(r / t - CR, g / t - CG) * 255;
      allow = chromaAllow;
      dl = lum(r, g, b) - baseL;
      mask[p] = (res <= C.TOL_SHADE && dc <= allow &&
                 dl >= -C.TOL_DARKER && dl <= C.TOL_LIGHTER) ? 1 : 0;
    }
    return mask;
  }


  /* The contiguous run that contains the centre column, per row, tolerating
   * small gaps so a drawstring does not cut the torso in half. */
  function rowRun(mask, w, row, seed) {
    var o = row * w;
    if (!mask[o + seed]) {
      var k, found = -1;
      for (k = 1; k < w * 0.25; k++) {
        if (seed - k >= 0 && mask[o + seed - k]) { found = seed - k; break; }
        if (seed + k < w && mask[o + seed + k]) { found = seed + k; break; }
      }
      if (found < 0) return null;
      seed = found;
    }
    var l = seed, r = seed, gap;
    for (gap = 0; l > 0 && gap < w * 0.035; l--) { gap = mask[o + l - 1] ? 0 : gap + 1; }
    for (gap = 0; r < w - 1 && gap < w * 0.035; r++) { gap = mask[o + r + 1] ? 0 : gap + 1; }
    return { l: l, r: r, w: r - l, c: ((l + r) / 2) | 0 };
  }

  /* The printable chest, derived from the SUBJECT silhouette rather than from
   * colour. Colour cannot do this job: a black hoodie in a dark workshop is the
   * same colour as the wall behind it, and the garment mask floods into the
   * background. The shoulders, on the other hand, are unambiguous, and every
   * proportion a print shop uses is measured from them.
   *
   * Returns null when it cannot find a clear front-facing torso, which is the
   * honest answer for a pose this version does not support. */
  function findQuad(subject, w, h, garment) {
    var rows = [], y, seed = (w / 2) | 0, run;
    for (y = (h * 0.04) | 0; y < (h * 0.92) | 0; y += 2) {
      run = rowRun(subject, w, y, seed);
      if (run && run.w > w * 0.03) { rows.push({ y: y, w: run.w, c: run.c, l: run.l, r: run.r }); seed = run.c; }
    }
    if (rows.length < 20) return null;

    /* The head is the narrow part at the top; the shoulders are where the
     * silhouette suddenly widens. */
    var headRows = rows.slice(0, Math.max(4, (rows.length * 0.10) | 0));
    var hw = headRows.map(function (r) { return r.w; }).sort(function (a, b) { return a - b; });
    var headW = hw[(hw.length / 2) | 0];
    var shoulderAt = -1, i;
    for (i = 0; i < rows.length; i++) {
      if (rows[i].w > headW * 1.55) { shoulderAt = i; break; }
    }
    if (shoulderAt < 0) return null;

    /* Measure AT the shoulder line, not the widest point below it: a little
     * lower and the arms have flared out, which inflates the figure and pushes
     * the whole print area out with it. */
    var off = Math.max(2, (rows.length * 0.02) | 0);
    var shoulderRow = rows[Math.min(rows.length - 1, shoulderAt + off)];
    var shoulderW = shoulderRow.w;
    if (!(shoulderW > w * 0.12)) return null;

    /* Two measurements, and we take the smaller.
     *   The silhouette's shoulder width is inflated by big hair - on the tee
     *   test it read 1077px against a torso nearer 700.
     *   The garment's own width at the chest is inflated the other way by long
     *   sleeves hanging at the sides, which is the hoodie case.
     * Neither is wrong often, and they are never wrong together, so the
     * smaller of the two is a good estimate of the body under the garment. */
    var top = shoulderRow.y + shoulderW * C.QUAD_TOP_OF_SHOULDER;
    var effective = shoulderW;
    if (garment) {
      var gr = rowRun(garment, w, Math.min(h - 1, Math.round(top + shoulderW * 0.25)), shoulderRow.c);
      if (gr && gr.w > w * 0.08) effective = Math.min(effective, gr.w);
    }
    var quadW = effective * C.QUAD_W_OF_SHOULDER;
    var bottom = top + quadW * C.QUAD_H_OF_W;
    var lastY = rows[rows.length - 1].y;
    if (bottom > lastY) bottom = lastY;
    if (bottom - top < quadW * 0.45) return null;

    /* One centre line, taken at the shoulders. The silhouette's centre lower
     * down follows the arms rather than the torso, and a quad that drifts with
     * it puts the print off-centre on the chest. */
    var cx = shoulderRow.c;
    return {
      tl: [cx - quadW / 2, top], tr: [cx + quadW / 2, top],
      br: [cx + quadW / 2, bottom], bl: [cx - quadW / 2, bottom],
      shoulder: { y: shoulderRow.y, w: shoulderW }, headW: headW
    };
  }

  /* The artwork's real edges. A customer's PNG usually has transparent margin,
   * and a selection box drawn on the file's edges rather than the design's
   * looks broken and makes the handles lie. Returned normalised. */
  function trimBounds(img) {
    var w = img.width, h = img.height, d = img.data;
    var x0 = w, y0 = h, x1 = -1, y1 = -1, x, y, i;
    for (y = 0; y < h; y++) {
      for (x = 0; x < w; x++) {
        i = (y * w + x) * 4 + 3;
        if (d[i] > 8) {
          if (x < x0) x0 = x;
          if (x > x1) x1 = x;
          if (y < y0) y0 = y;
          if (y > y1) y1 = y;
        }
      }
    }
    if (x1 < 0) return { x: 0, y: 0, w: 1, h: 1, empty: true };
    return { x: x0 / w, y: y0 / h, w: (x1 - x0 + 1) / w, h: (y1 - y0 + 1) / h, empty: false };
  }

  /* Flat lay and hanging garment: there is no head, so the shoulder rule does
   * not apply. The body width is taken below the sleeves, where the silhouette
   * is the garment body alone, and the print area is measured from that. */
  function findQuadGarment(subject, w, h) {
    var rows = [], y, seed = (w / 2) | 0, run;
    for (y = 0; y < h; y += 2) {
      run = rowRun(subject, w, y, seed);
      if (run && run.w > w * 0.04) { rows.push({ y: y, w: run.w, c: run.c }); seed = run.c; }
    }
    if (rows.length < 12) return null;
    var y0 = rows[0].y, y1 = rows[rows.length - 1].y, H = y1 - y0;
    if (H < h * 0.15) return null;
    var at = function (yy) {
      var best = rows[0], k;
      for (k = 0; k < rows.length; k++) if (Math.abs(rows[k].y - yy) < Math.abs(best.y - yy)) best = rows[k];
      return best;
    };
    var body = at(y0 + H * C.GARMENT_BODY_AT);
    var quadW = body.w * C.GARMENT_W_OF_BODY;
    var top = y0 + H * C.GARMENT_TOP_OF_H;
    var bottom = Math.min(y1, top + quadW * C.QUAD_H_OF_W);
    if (bottom - top < quadW * 0.45) return null;
    var cx = body.c;
    return {
      tl: [cx - quadW / 2, top], tr: [cx + quadW / 2, top],
      br: [cx + quadW / 2, bottom], bl: [cx - quadW / 2, bottom],
      shoulder: { y: top, w: body.w }, headW: 0, mode: 'garment'
    };
  }

  /* Bilinear point inside a quad: (u,v) in 0..1 -> image pixels. */
  function quadPoint(q, u, v) {
    var tx = q.tl[0] + (q.tr[0] - q.tl[0]) * u, ty = q.tl[1] + (q.tr[1] - q.tl[1]) * u;
    var bx = q.bl[0] + (q.br[0] - q.bl[0]) * u, by = q.bl[1] + (q.br[1] - q.bl[1]) * u;
    return [tx + (bx - tx) * v, ty + (by - ty) * v];
  }

  /* `img` is the generated scene; `subjectImg` is a matte of the person or
   * garment, same size, any single channel in the red byte. */
  /* Keep only components of a mask at or above `minFrac` of the picture, which
   * removes the handful of stray specks a segmentation model leaves on a cheek
   * or a wall without touching the garment itself. */
  function largestParts(mask, w, h, minFrac) {
    var n = w * h, out = new Uint8Array(n), seen = new Uint8Array(n);
    var q = new Int32Array(n), comp = new Int32Array(n);
    var floor = Math.max(64, Math.round(n * (minFrac || 0.002)));
    for (var s0 = 0; s0 < n; s0++) {
      if (!mask[s0] || seen[s0]) continue;
      var head = 0, tail = 0, cn = 0, p, x, y;
      q[tail++] = s0; seen[s0] = 1;
      while (head < tail) {
        p = q[head++]; comp[cn++] = p; x = p % w; y = (p - x) / w;
        if (x > 0 && mask[p - 1] && !seen[p - 1]) { seen[p - 1] = 1; q[tail++] = p - 1; }
        if (x < w - 1 && mask[p + 1] && !seen[p + 1]) { seen[p + 1] = 1; q[tail++] = p + 1; }
        if (y > 0 && mask[p - w] && !seen[p - w]) { seen[p - w] = 1; q[tail++] = p - w; }
        if (y < h - 1 && mask[p + w] && !seen[p + w]) { seen[p + w] = 1; q[tail++] = p + w; }
      }
      if (cn >= floor) for (var i = 0; i < cn; i++) out[comp[i]] = 1;
    }
    return out;
  }

  /* `garmentImg` - when present - is a TRUSTED garment layer: a segmentation of
   * the cloth itself rather than our inference from colour.
   *
   * This is the whole difference. Everything the compositor got wrong came from
   * one place: it was handed a finished photograph and had to guess which pixels
   * were cloth. On a brown garment that is not a hard problem, it is an ill-posed
   * one - brown cloth, brown hair and skin share a hue, so no threshold can
   * separate them, and every fix traded one failure for another. Given the layer
   * by name, the guessing stops:
   *
   *   clip        = the garment layer, so folds and deep shadow stay garment and
   *                 never cut a hole in the artwork
   *   foreground  = inside the person and NOT the garment, so hair, hands,
   *                 collars and drawstrings come forward with their own contours
   *                 instead of a detector's idea of them
   *
   * The cord and hair passes are switched off in this mode: they exist to
   * approximate what this layer states outright, and they can only add artifacts
   * on top of it. */
  function analyse(img, subjectImg, mode, garmentImg) {
    var w = img.width, h = img.height, data = img.data;
    var subject = new Uint8Array(w * h), i, p;
    if (subjectImg) {
      var sd = subjectImg.data;
      for (p = 0, i = 0; p < subject.length; p++, i += 4) subject[p] = sd[i] > 128 ? 1 : 0;
    } else {
      for (p = 0; p < subject.length; p++) subject[p] = 1;
    }
    var col = garmentColour(data, w, h);
    /* The garment is what matches the chest colour AND is part of the subject.
     * The second half is what keeps a black hoodie from merging with a dark
     * wall behind it. */
    var mask = makeMask(data, w, h, col);
    for (p = 0; p < mask.length; p++) if (!subject[p]) mask[p] = 0;
    mask = closeMask(mask, w, h, Math.max(3, Math.round(w / 400)));

    /* On a model we measure from the shoulders. Flat lay and hanging garment
     * have none, so they use the garment's own body width instead. */
    var quad = (mode === 'flat' || mode === 'hanging')
      ? findQuadGarment(subject, w, h)
      : findQuad(subject, w, h, mask);
    if (!quad) return null;

    /* A second, looser definition of the garment's surface, used for deciding
     * what the print covers and what covers the print.
     *
     * The mask above also judges brightness, which is right for finding the
     * chest and for spotting a print, and wrong here: a crease is the garment
     * at a fraction of the light, and judging it on brightness is what cut
     * lines through the artwork. This one asks only "is this the garment's
     * COLOUR" - so a crease, a deep shadow and a highlight are all still the
     * garment, while hair, skin and anything printed on it are not. */
    /* A trusted garment layer replaces the whole inference chain. */
    var parsed = null;
    if (garmentImg) {
      var gd = garmentImg.data, gw = garmentImg.width, gh = garmentImg.height;
      parsed = new Uint8Array(w * h);
      /* Sampled BILINEARLY, not nearest.
       *
       * The layer comes back at the model's own working size, smaller than the
       * scene, so it has to be scaled up. Taking the nearest pixel reproduces
       * that grid as visible stair-steps along the neckline - small, but it is
       * the "pasted on" look, and it is exactly the kind of blockiness this
       * whole rebuild is meant to remove. Interpolating first and thresholding
       * after puts the edge where it actually falls, between samples. */
      var gx, gy, fx, fy, x0i, y0i, x1i, y1i, ax, ay, v;
      for (gy = 0; gy < h; gy++) {
        fy = (gy + 0.5) * gh / h - 0.5;
        y0i = Math.max(0, Math.min(gh - 1, Math.floor(fy)));
        y1i = Math.max(0, Math.min(gh - 1, y0i + 1));
        ay = fy - Math.floor(fy); if (ay < 0) ay = 0;
        for (gx = 0; gx < w; gx++) {
          fx = (gx + 0.5) * gw / w - 0.5;
          x0i = Math.max(0, Math.min(gw - 1, Math.floor(fx)));
          x1i = Math.max(0, Math.min(gw - 1, x0i + 1));
          ax = fx - Math.floor(fx); if (ax < 0) ax = 0;
          v = (gd[(y0i * gw + x0i) * 4] * (1 - ax) + gd[(y0i * gw + x1i) * 4] * ax) * (1 - ay)
            + (gd[(y1i * gw + x0i) * 4] * (1 - ax) + gd[(y1i * gw + x1i) * 4] * ax) * ay;
          parsed[gy * w + gx] = v > 128 ? 1 : 0;
        }
      }
      /* Specks on a cheek or a wall are not the garment. */
      parsed = largestParts(parsed, w, h, 0.004);
      /* The matte bounds it a few lines below, where every surface is clamped
       * to the grown subject - so a mask that strays onto the background still
       * cannot let the print follow it there. */
    }

    /* Seeds on the chest. The quad spans the printable area, so points well
     * inside it are cloth whatever colour the cloth happens to be. */
    var seeds = [], su, sv, SP;
    for (sv = 0.2; sv <= 0.8; sv += 0.1) {
      for (su = 0.25; su <= 0.75; su += 0.1) {
        SP = quadPoint(quad, su, sv);
        seeds.push([Math.min(w - 1, Math.max(0, SP[0] | 0)), Math.min(h - 1, Math.max(0, SP[1] | 0))]);
      }
    }
    var surfaceMask = parsed ||
        connectSurface(seeds, chromaMask(data, w, h, col, 1), data, w, h);
    /* A second, stricter reading of the same test. Anything that fails THIS is
     * "possibly not the garment" - used only to grow a foreground region that
     * the confident test already started. Blonde hair on a brown sweatshirt is
     * the case: some strands are clearly a different colour and some are within
     * a hair's breadth of the fabric, so a single threshold lifts half a head
     * of hair and leaves the rest lying under the print, which looks worse than
     * either extreme. */
    var surfaceTight = parsed ||
        connectSurface(seeds, chromaMask(data, w, h, col, 0.5), data, w, h);
    /* The matte is grown a little before it is used to cut the surface.
     *
     * birefnet's silhouette is close but not exact, and where it falls a few
     * pixels INSIDE the real garment the consequence is severe: those pixels
     * are not "the garment", so the print is clipped off them AND the real
     * fabric is painted back over the print - a smooth wedge of cloth eating
     * the corner of an opaque image. Growing the matte by a fraction of the
     * picture's width absorbs that error. It cannot leak the print far onto the
     * background, because a pixel still has to be the garment's colour too. */
    var grownSubject = growMask(subject, w, h, Math.max(4, Math.round(w / 120)));
    for (p = 0; p < surfaceMask.length; p++) if (!grownSubject[p]) surfaceMask[p] = 0;
    for (p = 0; p < surfaceTight.length; p++) if (!grownSubject[p]) surfaceTight[p] = 0;
    /* Not over a parsed layer: closing would bridge the gap a drawstring
     * leaves in the cloth, and that gap is precisely what puts the cord in
     * front of the print. */
    /* Morphological smoothing of this boundary was tried and reverted. The
     * ragged edge it was meant to fix is not speckle - it is the garment's real
     * outline where a hem meets trousers, legs and the shadow between them, and
     * opening at any radius small enough to keep the sleeves left it unchanged
     * while costing a little printable area. The hem is handled where it
     * belongs instead: the default placement keeps clear of it, and a design
     * dragged past it says so. */
    if (!parsed) surfaceMask = closeMask(surfaceMask, w, h, Math.max(3, Math.round(w / 400)));

    var sw = Math.max(32, (w / 10) | 0), sh = Math.max(32, (h / 10) | 0);
    var L = new Float32Array(sw * sh), x, y, sx, sy;
    for (y = 0; y < sh; y++) {
      sy = ((y + 0.5) * h / sh) | 0;
      for (x = 0; x < sw; x++) {
        sx = ((x + 0.5) * w / sw) | 0; i = (sy * w + sx) * 4;
        L[y * sw + x] = lum(data[i], data[i + 1], data[i + 2]);
      }
    }
    var Lb = boxBlur(L, sw, sh, Math.max(2, (sw / 48) | 0));
    var vals = [], u, v, P;
    for (v = 0.05; v < 0.95; v += 0.05) {
      for (u = 0.05; u < 0.95; u += 0.05) {
        P = quadPoint(quad, u, v);
        sx = Math.min(sw - 1, Math.max(0, (P[0] * sw / w) | 0));
        sy = Math.min(sh - 1, Math.max(0, (P[1] * sh / h) | 0));
        if (mask[(((P[1] | 0) * w) + (P[0] | 0))]) vals.push(Lb[sy * sw + sx]);
      }
    }
    vals.sort(function (a, b) { return a - b; });
    var median = vals.length ? vals[(vals.length / 2) | 0] : 128;
    return { w: w, h: h, colour: col, mask: mask, surfaceMask: surfaceMask,
             surfaceTight: surfaceTight, mode: mode,
             surfaceSource: parsed ? 'parsed' : 'inferred',
             subject: subject, quad: quad,
             light: { data: Lb, w: sw, h: sh, median: median || 1 } };
  }


  /* Is there enough layer evidence to let somebody place artwork on this scene?
   *
   * Every complaint about this tool has the same shape: the customer spends time
   * positioning a design and only then sees that the garment was never
   * understood. The honest moment to find that out is BEFORE placement, not
   * after - so this asks four questions of the layers themselves, and a scene
   * that cannot answer them is refused with a template offered instead.
   *
   * It deliberately measures the surface against things that do NOT come from
   * the surface: the subject matte and the garment quad. A check that asks the
   * suspect mask to vouch for itself is the mistake that let a broken scene
   * through before.
   */
  function layerEvidence(surface) {
    var w = surface.w, h = surface.h, n = w * h, p;
    var cloth = surface.surfaceMask || surface.mask, subject = surface.subject;
    var clothN = 0, subjN = 0, clothOutside = 0;
    for (p = 0; p < n; p++) {
      if (cloth[p]) {
        clothN++;
        if (!subject[p]) clothOutside++;
      }
      if (subject[p]) subjN++;
    }
    /* 1. the printable area actually exists */
    var clothFrac = clothN / n;
    /* 2. the garment is a sensible share of the person, not a sliver and not
     *    the whole silhouette (which would mean skin and hair were swallowed) */
    var ofSubject = subjN ? clothN / subjN : 0;
    /* 3. the quad we would place into is really covered by cloth */
    var cover = 0, coverN = 0, u, v, P, q;
    for (v = 0.15; v <= 0.85001; v += 0.05) {
      for (u = 0.15; u <= 0.85001; u += 0.05) {
        P = quadPoint(surface.quad, u, v);
        q = ((P[1] | 0) * w) + (P[0] | 0);
        if (q < 0 || q >= n) continue;
        coverN++;
        if (cloth[q]) cover++;
      }
    }
    var quadCover = coverN ? cover / coverN : 0;
    /* 4. the garment is not leaking off the person onto the background */
    var leak = clothN ? clothOutside / clothN : 0;
    /* 5. the garment does not run far BELOW its own printable area.
     *
     * This replaces a ratio of garment-to-person that no longer works. When
     * scenes were framed head to mid-thigh, a garment covering nearly all of
     * the visible person meant the surface had swallowed them. Framed at the
     * chin, as a mockup actually is, the garment legitimately IS nearly all of
     * the visible person - so that ratio fails a good scene and a bad one
     * alike.
     *
     * What actually went wrong in the bad case was positional: a black hoodie
     * over dark denim, and the surface ran straight down over the trousers to
     * the bottom of the frame. So measure that directly - how far past the
     * bottom of the garment quad the surface reaches, in units of the quad's
     * own height. */
    var qTop = quadPoint(surface.quad, 0.5, 0), qBot = quadPoint(surface.quad, 0.5, 1);
    var quadH = Math.max(1, Math.abs(qBot[1] - qTop[1]));
    var lowest = 0, highest = h;
    for (p = 0; p < n; p++) {
      if (!cloth[p]) continue;
      var py = (p - (p % w)) / w;
      if (py > lowest) lowest = py;
      if (py < highest) highest = py;
    }
    var below = (lowest - qBot[1]) / quadH;
    var above = (qTop[1] - highest) / quadH;

    var reasons = [];
    if (clothFrac < 0.04) reasons.push('garment-too-small');
    if (ofSubject < 0.15) reasons.push('garment-not-found');
    /* There is NO cheap test here for "the surface swallowed the person", and
     * that is a measured finding rather than an omission.
     *
     * Three candidates were tried against two known-bad surfaces (one that ate
     * a model's face, one that ate his jeans) and four known-good ones:
     *
     *   garment-to-person ratio   bad 1.042, 1.049   good 0.503 - 1.013
     *   extent below the quad     bad 0.990, 0.158   good 0.061 - 0.239
     *   extent above the quad     bad 0.907, 0.827   good 0.477 - 1.471
     *
     * Every one of them overlaps. The ratio worked when scenes were framed head
     * to mid-thigh; cropped at the chin, as a mockup actually is, the garment
     * legitimately IS nearly all of the visible person, and a good scene is
     * indistinguishable from a bad one by any of these.
     *
     * So the cause is addressed where it starts - the scene brief now asks for a
     * visible hem and contrasting trousers, which fixed the jeans case outright -
     * and the trusted garment layer remains the only thing that detects it
     * reliably when it does happen. Shipping a geometric rule here would only
     * buy false confidence: the last one I tried PASSED both known-bad cases. */
    if (quadCover < 0.90) reasons.push('print-area-not-covered');
    if (leak > 0.12) reasons.push('garment-leaks-off-person');
    return {
      ok: reasons.length === 0, reasons: reasons,
      clothFraction: +clothFrac.toFixed(4), garmentOfSubject: +ofSubject.toFixed(4),
      quadCoverage: +quadCover.toFixed(4), leakOffSubject: +leak.toFixed(4),
      belowQuad: +below.toFixed(3), aboveQuad: +above.toFixed(3),
      source: surface.surfaceSource || 'inferred'
    };
  }

  /* ---------------------------------------------------------- placement */

  /* `w` and `h` are fractions of the chest quad's width and height. Keeping
   * both explicit is what lets the customer unlock proportions: locked simply
   * means h is recomputed from w and the artwork's own aspect. */
  function fitHeight(surface, w, artRatio) {
    var q = surface.quad;
    var quadW = Math.hypot(q.tr[0] - q.tl[0], q.tr[1] - q.tl[1]);
    var quadH = Math.hypot(q.bl[0] - q.tl[0], q.bl[1] - q.tl[1]);
    return (w * quadW) * artRatio / quadH;
  }

  /* Where a print starts, in the garment quad's own (u,v) space.
   *
   * The old default was a fixed width and a fixed centre, which ignored the
   * artwork's shape: a tall photo at 0.92 of the quad's width comes out taller
   * than the quad itself, so it ran straight off the bottom and was clipped
   * against the hem, the trousers and the shadow between the legs. That edge is
   * the garment's real outline and it is never going to look tidy, so the fix
   * is not to land there in the first place.
   *
   * A print now starts inside a band with the hem left clear, and a tall design
   * is scaled to fit that band rather than spilling past it. The customer can
   * still drag and resize anywhere afterwards - nothing is locked - they just
   * do not begin in the one place that looks broken. */
  /* How much of the placed artwork is NOT on the garment.
   *
   * Used for an advisory, never a restriction: placement stays completely free.
   * It answers one question - would the customer see a torn-looking edge - so
   * it only counts artwork that is off the cloth, which is exactly what gets
   * clipped against a hem or a sleeve. */
  function offGarment(surface, pl) {
    var a = artQuadUV(surface, pl), cloth = surface.surfaceMask || surface.mask;
    var inside = 0, total = 0, u, v, P, q;
    for (v = 0.02; v <= 0.98; v += 0.04) {
      for (u = 0.02; u <= 0.98; u += 0.04) {
        P = quadPoint(surface.quad, a.u0 + (a.u1 - a.u0) * u, a.v0 + (a.v1 - a.v0) * v);
        total++;
        q = ((P[1] | 0) * surface.w) + (P[0] | 0);
        if (q >= 0 && q < cloth.length && cloth[q]) inside++;
      }
    }
    return total ? 1 - inside / total : 0;
  }

  function defaultPlacement(surface, artRatio) {
    /* Start at the top of the chest and take the largest size that still sits
     * entirely ON the cloth.
     *
     * A fixed band in quad units was the first attempt and it over-shrank: the
     * quad is short on some garments, so v=0.78 is physically high up and a tall
     * photo came out at a third of the chest width with empty fabric below it.
     * The thing we actually care about is whether the print is on the garment,
     * so measure that instead of guessing at a proxy - a few hundred samples
     * against the real mask, a handful of times, which is nothing next to a
     * render. */
    var TOP = 0.08, LIMIT = 0.95;
    var best = null, lo = 0.30, hi = C.DEFAULT_WIDTH;
    var tryW = function (w) {
      var h = fitHeight(surface, w, artRatio);
      if (h > LIMIT - TOP) return null;
      var pl = { cx: C.DEFAULT_CX, cy: TOP + h / 2, w: w, h: h,
                 ratio: artRatio, locked: true };
      return offGarment(surface, pl) <= 0.01 ? pl : null;
    };
    /* Eight bisection steps land within about half a percent of the largest
     * width that fits, which is finer than anyone can see. */
    for (var i = 0; i < 8; i++) {
      var mid = (lo + hi) / 2;
      var got = tryW(mid);
      if (got) { best = got; lo = mid; } else { hi = mid; }
    }
    if (!best) best = tryW(lo) || tryW(0.30);
    if (!best) {
      var hh = fitHeight(surface, 0.30, artRatio);
      best = { cx: C.DEFAULT_CX, cy: TOP + hh / 2, w: 0.30, h: hh,
               ratio: artRatio, locked: true };
    }
    return best;
  }

  /* The artwork's own quad, expressed in the garment quad's (u,v) space, so it
   * inherits the garment's perspective wherever the customer puts it. */
  function artQuadUV(surface, pl) {
    var wu = pl.w !== undefined ? pl.w : pl.width;
    var hv = pl.h !== undefined ? pl.h : fitHeight(surface, wu, pl.ratio);
    return { u0: pl.cx - wu / 2, u1: pl.cx + wu / 2, v0: pl.cy - hv / 2, v1: pl.cy + hv / 2 };
  }



  function sampleLight(surface, px, py) {
    var l = surface.light;
    var sx = Math.min(l.w - 1, Math.max(0, (px * l.w / surface.w) | 0));
    var sy = Math.min(l.h - 1, Math.max(0, (py * l.h / surface.h) | 0));
    return l.data[sy * l.w + sx];
  }

  /* Gentle fold displacement: push along the light gradient, never more than a
   * hair of the print width, so lettering rides the fold instead of melting. */
  function displace(surface, px, py, amp) {
    var d = Math.max(2, surface.w / 220);
    var gx = sampleLight(surface, px + d, py) - sampleLight(surface, px - d, py);
    var gy = sampleLight(surface, px, py + d) - sampleLight(surface, px, py - d);
    var n = 42;
    return [Math.max(-1, Math.min(1, gx / n)) * amp, Math.max(-1, Math.min(1, gy / n)) * amp];
  }

  /* Is the generated garment actually blank where we want to print?
   * A plain garment is nearly colourless there, whatever its colour: black,
   * white and heather all sit near zero chroma, while a printed graphic does
   * not. Folds change brightness, not chroma, so chroma is the honest test. */
  /* Is this garment blank, or did the model print something on it?
   *
   * The first version asked whether chest pixels were "colourful" - saturation
   * above a fixed threshold. That works only if the garment is black, white,
   * grey or cream. On a red, royal, orange, yellow, pink, purple, brown, tan,
   * light blue or burgundy garment the FABRIC is saturated, so every sample
   * counted as a graphic and a perfectly clean render was thrown away three
   * times in a row. Ten of the eighteen colours we offer could never succeed.
   *
   * So the question is no longer "is this colourful" but "is part of this chest
   * a different colour from the rest of it, over a large connected area".
   *
   *  - The reference is the garment's own median colour, so any fabric colour
   *    is the baseline rather than a suspect.
   *  - Comparison is by chromaticity, which folds and shading barely move,
   *    plus a brightness test for the dark-print-on-dark-shirt case that
   *    chromaticity cannot see.
   *  - Both thresholds come from the garment's own robust spread, so a mottled
   *    heather is not held to the same tolerance as flat jersey.
   *  - A print is one object. Scattered samples - a seam, a drawstring, a
   *    shadow in a fold - are not, so only the largest connected run counts.
   *
   * It errs towards accepting. A missed graphic costs the customer one look at
   * a wrong mockup; a false alarm costs three paid generations and ends in a
   * dead end, which is what happened. */
  /* KNOWN LIMIT, measured rather than assumed. A chest logo smaller than about
   * a sixth of the picture's width - roughly a 3in print - is not reliably
   * separable from clean fabric by this method. Its connected blob is 0.020
   * while the noisiest clean garment reaches 0.042, so no threshold separates
   * them; and a finer sampling lattice makes it worse, not better, because the
   * logo shrinks relative to the chest while fabric noise grows with it
   * (measured: at N=72 the logo falls to 0.012 and clean fabric rises to 0.075).
   *
   * Left as it is on purpose. Every graphic the model has actually produced
   * uninvited has been large, and all seven of those are caught. The cost of
   * going tighter is the failure that prompted this work: clean garments
   * rejected, three paid generations burnt, and a customer dead-ended. */
  function blankness(surface, img) {
    var q = surface.quad, d = img.data, m = surface.mask, w = surface.w, h = surface.h;
    var N = 36, pts = [], i, j, u, v, P, px, py, k;

    for (j = 0; j < N; j++) {
      for (i = 0; i < N; i++) {
        u = 0.06 + (0.89 * i) / (N - 1);
        v = 0.06 + (0.89 * j) / (N - 1);
        P = quadPoint(q, u, v);
        px = P[0] | 0; py = P[1] | 0;
        if (px < 0 || py < 0 || px >= w || py >= h) { pts.push(null); continue; }
        if (!m[py * w + px]) { pts.push(null); continue; }
        k = (py * w + px) * 4;
        pts.push([d[k], d[k + 1], d[k + 2]]);
      }
    }
    var live = pts.filter(Boolean);
    /* How much of the chest reads as bare garment at all. A big print pushes
     * this down on its own, and when almost none of the chest is cloth there is
     * nothing left to analyse - that is not a reason to wave it through, it is
     * the answer. */
    var inQuad = 0;
    for (i = 0; i < pts.length; i++) {
      P = quadPoint(q, 0.06 + (0.89 * (i % N)) / (N - 1), 0.06 + (0.89 * ((i / N) | 0)) / (N - 1));
      px = P[0] | 0; py = P[1] | 0;
      if (px >= 0 && py >= 0 && px < w && py < h) inQuad++;
    }
    /* How much of the chest still reads as bare garment.
     *
     * This is the signal that holds up on a dark garment, where hue is noisy
     * and the blob measure has to be given room. Measured across the saved
     * scenes: every clean garment sits between 0.961 and 1.000, while a black
     * hoodie with a print on it drops to 0.59-0.61 and a large print takes it
     * to 0.03. There is no overlap and a wide gap, so this carries the dark
     * cases that the colour route cannot. */
    var coverage = inQuad ? live.length / inQuad : 0;
    if (coverage < 0.80) {
      return { ok: false, reason: 'chest-covered', counted: live.length,
               coverage: +coverage.toFixed(3) };
    }
    if (live.length < 60) {
      return { ok: true, reason: 'too-few-samples', counted: live.length,
               coverage: +coverage.toFixed(3) };
    }

    /* The garment's own colour, as a median per channel. A graphic has to cover
     * more than half the chest before it can move this. */
    function med(a) { var b = a.slice().sort(function (x, y) { return x - y; }); return b[(b.length / 2) | 0]; }
    var R = med(live.map(function (c) { return c[0]; }));
    var G = med(live.map(function (c) { return c[1]; }));
    var B = med(live.map(function (c) { return c[2]; }));

    /* Chromaticity, with a floor under the denominator so a near-black garment
     * does not turn pixel noise into a hue. */
    function chroma(c) {
      var t = c[0] + c[1] + c[2] + 60;
      return [c[0] / t, c[1] / t];
    }
    var ref = chroma([R, G, B]), refL = lum(R, G, B);

    /* Brightness is compared LOCALLY, not against one number for the whole
     * chest. The shadow under a hood is a big smooth gradient - it moves the
     * global comparison a long way and a print does not have to be very dark to
     * hide underneath it. Against a blurred copy of the chest, shading cancels
     * out and only the sharp-edged things a print is made of survive.
     * Colour stays a global comparison; shading barely moves it. */
    var grid = new Float32Array(N * N), have = new Uint8Array(N * N);
    for (i = 0; i < pts.length; i++) {
      if (!pts[i]) continue;
      grid[i] = lum(pts[i][0], pts[i][1], pts[i][2]);
      have[i] = 1;
    }
    /* Fill the gaps with the chest median so the blur is not dragged by holes. */
    for (i = 0; i < N * N; i++) if (!have[i]) grid[i] = refL;
    var smooth = boxBlur(grid, N, N, 4);

    var dc = [], dl = [], c2, p3 = 0;
    for (i = 0; i < pts.length; i++) {
      if (!pts[i]) continue;
      c2 = chroma(pts[i]);
      dc.push(Math.hypot(c2[0] - ref[0], c2[1] - ref[1]) * 255);
      dl.push(Math.abs(grid[i] - smooth[i]));
      p3++;
    }
    /* The spread has to be measured on the FABRIC, not on the fabric plus
     * whatever is printed on it. A median is not low enough: a graphic across
     * the middle of the chest can cover more than half the samples, and then
     * the median deviation is the graphic's, the threshold rises to meet it,
     * and the check quietly stops seeing anything. The fifth percentile is
     * still plain fabric even when a print covers most of the chest. */
    function pct(a, f) {
      var b = a.slice().sort(function (x, y) { return x - y; });
      return b[Math.min(b.length - 1, Math.max(0, Math.round((b.length - 1) * f)))];
    }
    /* Capped as well as floored. If even the quietest twentieth of the chest
     * deviates a lot, the estimate has been dragged up by the print itself, and
     * letting the threshold follow it is how a graphic hides in plain sight. */
    function clamp(v, lo, hi) { return v < lo ? lo : (v > hi ? hi : v); }
    /* Hue is only as reliable as the light coming off the cloth. On a near-black
     * garment the colour of a pixel is mostly sensor noise, so the same chroma
     * tolerance that is right on a white tee marks fabric all over a black
     * hoodie. Measured across the saved scenes, the two garments that sat
     * closest to the rejection line were the black hoodie (0.072) and the grey
     * tee (0.078) against a 0.115 limit - and on both, essentially ALL of that
     * marking came from the chroma route and almost none from brightness.
     * A different black hoodie, in a different light, is how that tips over.
     * So the chroma tolerance scales with how dark the garment is. */
    var darkFactor = Math.max(1, 380 / (R + G + B + 60));
    var tC = clamp(pct(dc, 0.05) * 9, 12 * darkFactor, 40 * darkFactor);
    var tL = clamp(pct(dl, 0.05) * 9, 40, 110);

    /* Mark the lattice, then measure the largest connected run of marks. */
    var mark = new Uint8Array(N * N), p2 = 0, x, y;
    for (i = 0; i < pts.length; i++) {
      if (!pts[i]) continue;
      if (dc[p2] > tC || dl[p2] > tL) mark[i] = 1;
      p2++;
    }
    /* Close one-cell gaps first. Artwork is not a solid colour - white letters
     * on a white garment match the fabric exactly - so the marks come out as a
     * constellation rather than a shape, and strict neighbour-touching
     * connectivity finds nothing. One round of growing joins the pieces of one
     * print without joining two unrelated specks. */
    var marked = 0;
    for (i = 0; i < N * N; i++) if (mark[i]) marked++;

    var grown = new Uint8Array(N * N);
    for (i = 0; i < N * N; i++) {
      if (!mark[i]) continue;
      x = i % N; y = (i / N) | 0;
      for (j = -1; j <= 1; j++) {
        for (k = -1; k <= 1; k++) {
          var gx = x + k, gy = y + j;
          if (gx >= 0 && gx < N && gy >= 0 && gy < N) grown[gy * N + gx] = 1;
        }
      }
    }
    mark = grown;

    var seen = new Uint8Array(N * N), stack = [], best = 0, size, cur, nb;
    for (i = 0; i < N * N; i++) {
      if (!mark[i] || seen[i]) continue;
      stack.length = 0; stack.push(i); seen[i] = 1; size = 0;
      while (stack.length) {
        cur = stack.pop(); size++;
        x = cur % N; y = (cur / N) | 0;
        nb = [x > 0 ? cur - 1 : -1, x < N - 1 ? cur + 1 : -1,
              y > 0 ? cur - N : -1, y < N - 1 ? cur + N : -1];
        for (j = 0; j < 4; j++) {
          k = nb[j];
          if (k >= 0 && mark[k] && !seen[k]) { seen[k] = 1; stack.push(k); }
        }
      }
      if (size > best) best = size;
    }
    var biggest = best / live.length;

    /* Two independent routes to a rejection, because one number alone has a
     * thin margin. Measured over 25 cases - six real bases, eleven clean
     * garments in colours the old check could never pass, and eight garments
     * that really do carry a graphic - the worst clean garment reaches a blob
     * of 0.09 and a marked fraction of 0.06, while the weakest true positive,
     * a small chest logo, reaches 0.14 and 0.05. So the blob catches the small
     * logo and the marked fraction catches the broad prints, and neither has to
     * be set tight enough to start failing honest renders. Re-tuned after the
     * black-hoodie failure: the worst clean garment now blobs at 0.042. */
    var fMarked = marked / live.length;
    return {
      ok: !(biggest >= 0.09 || fMarked >= 0.12),
      biggestBlob: +biggest.toFixed(4),
      markedFraction: +fMarked.toFixed(4),
      counted: live.length,
      coverage: +coverage.toFixed(3),
      garment: [R, G, B],
      thresholds: { chroma: +tC.toFixed(2), light: +tL.toFixed(2) }
    };
  }


  return {
    C: C, lum: lum, boxBlur: boxBlur, garmentColour: garmentColour, makeMask: makeMask,
    rowRun: rowRun, findQuad: findQuad, findQuadGarment: findQuadGarment,
    trimBounds: trimBounds, fitHeight: fitHeight, blankness: blankness, quadPoint: quadPoint, analyse: analyse,
    defaultPlacement: defaultPlacement, artQuadUV: artQuadUV,
    offGarment: offGarment,
    sampleLight: sampleLight, displace: displace, layerEvidence: layerEvidence,
    growMask: growMask, closeMask: closeMask, openMask: openMask,
    connectSurface: connectSurface,
    largestParts: largestParts,
    cordMask: cordMask, fillHoles: fillHoles,
    chromaMask: chromaMask
  };
}));
